import AddIcon from "@mui/icons-material/Add";
import { Button, Switch } from "@mui/material";
import cx from "classnames";
import { Dictionary } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";

import DotElastic from "components/DotElastic";
import Guide from "components/Guide";
import Layers, { LayersRef } from "components/Layers";
import SmartVectorsOutputGuideModal from "components/Layers/SmartVectorsOutputGuideModal";
import ReviewPanel from "components/ReviewPanel";
import { CameraTrack } from "components/Services/CameraTrack/CameraTrack";
import CreateDepthMap from "components/Services/DepthMap";
// import AddRetime from "components/Services/Retime/AddRetime";
// import RunAutopilot from "components/Services/RunAutopilot";
import ServiceDropdown from "components/Services/ServiceDropdown";
import { useSmartVectorsAddRoi } from "components/Services/SmartVectors/useSmartVectorsAddRoi";
import ExportPanel, { ExportButton } from "components/Shot/ExportPanel";
import ShotContext, { ShotContextType } from "components/Shot/ShotContext";
import SmartVectorsHelpButton from "components/SmartVectorsHelpButton";
import VideoPreview from "components/VideoPreview";
import Workspace from "components/Workspace";
import {
  UserGuides,
  LAYER_COLORS,
  SERVICES,
  WINDOW_EVENTS,
} from "constants/constants";
import { UserContext } from "layouts/SlapshotMainLayout";
import { Cutout, CutoutInProgress } from "models/Cutout";
import { LayerCompact, LayerReview } from "models/Layer";
import { Project } from "models/Project";
import { Subscription, ThrottleUsage } from "models/User";
import { getCutouts } from "services/CutoutService";
import { getLayers } from "services/LayerService";
import { getShotDetail, updateUseHq } from "services/ShotService";
import { getThrottleUsageApi } from "services/SubscriptionService";

import styles from "./Shot.module.scss";
import { ShotCompact, Shot as ShotType } from "../../models/Shot";

interface ShotProps {
  selectedShot: ShotType | null;
  setSelectedShot: React.Dispatch<React.SetStateAction<ShotType | null>>;
  project: Project;
  setShots: React.Dispatch<React.SetStateAction<Dictionary<ShotCompact[]>>>;
  service: SERVICES;
  setService: React.Dispatch<React.SetStateAction<SERVICES>>;
  subscription: Subscription | null;
}

export interface ShotRef {
  onInferenceLayerUpdate: (id: number) => void;
}

const Shot = ({ ref }: { ref?: React.Ref<ShotRef> }) => {
  const {
    shotId,
    layerId,
    project,
    selectedShot,
    setSelectedShot,
    isSelectedShotLoading,
    setIsSelectedShotLoading,
    layers,
    setLayers,
    selectedLayer,
    setSelectedLayer,
    setShotCutouts,
    imageData,
    selectedFrame,
    showExportPanel,
    setShowExportPanel,
    layersForExport,
    clearAnnotationData,
    isAnnotating,
    service,
    setShowTrackingPanel,
    setSmartVectorsReviewPane,
    exitAnnotationModeRef,
    requestSmartVectorsAddRoiRef,
  } = useContext(ShotContext);
  const user = useContext(UserContext);
  const layersRef = useRef<LayersRef>(null);
  const [showSecondTrackingTip, setSecondShowTrackingTip] = useState(false);
  const [addRoiIntroModalOpen, setAddRoiIntroModalOpen] = useState(false);
  const navigate = useNavigate();
  const addSmartVectorsRegionOfInterest = useSmartVectorsAddRoi();

  /** Same preference gate as "Not happy with this output?" (banner): modal only while that tip can still show. */
  const skipSmartVectorsAddRoiIntroModal = useMemo(() => {
    const prefs = user?.preferences;
    if (prefs === undefined) return true;
    if (prefs.hideTips) return true;
    const tips = prefs.tips || [];
    return tips.includes(UserGuides.SmartVectorsOutputGuide);
  }, [user?.preferences]);

  const requestSmartVectorsAddRoi = useCallback(() => {
    if (skipSmartVectorsAddRoiIntroModal) {
      addSmartVectorsRegionOfInterest();
      return;
    }
    setAddRoiIntroModalOpen(true);
  }, [addSmartVectorsRegionOfInterest, skipSmartVectorsAddRoiIntroModal]);

  useEffect(() => {
    if (service !== SERVICES.SMART_VECTORS) return undefined;
    requestSmartVectorsAddRoiRef.current = requestSmartVectorsAddRoi;
    return () => {
      requestSmartVectorsAddRoiRef.current = null;
    };
  }, [requestSmartVectorsAddRoi, requestSmartVectorsAddRoiRef, service]);

  const exitAnnotationMode = useCallback(() => {
    clearAnnotationData();
    if (selectedShot) {
      getCutouts(undefined, selectedShot.id, service).then((result) => {
        setShotCutouts(result);
      });
    }
    setSelectedLayer(null);
    if (
      (service === SERVICES.TRACKING || service === SERVICES.SMART_VECTORS) &&
      layerId &&
      selectedShot
    ) {
      navigate(`/projects/${project.id}/${service}/shots/${selectedShot.id}`);
    }
    if (layerId) {
      layersRef.current?.refreshCutouts();
    }
  }, [
    clearAnnotationData,
    layerId,
    navigate,
    project.id,
    selectedShot,
    service,
    setSelectedLayer,
    setShotCutouts,
  ]);

  useEffect(() => {
    exitAnnotationModeRef.current = exitAnnotationMode;
    return () => {
      exitAnnotationModeRef.current = null;
    };
  }, [exitAnnotationMode, exitAnnotationModeRef]);

  React.useImperativeHandle(ref, () => ({
    onInferenceLayerUpdate(id) {
      layersRef.current?.onInferenceLayerUpdate(id);
    },
  }));

  useEffect(() => {
    setSelectedShot(null);
    clearAnnotationData();
    setSelectedLayer(null);
    if (shotId) {
      setIsSelectedShotLoading(true);
      getShotDetail(+shotId)
        .then((result) => {
          setSelectedShot(result);
        })
        .finally(() => {
          setIsSelectedShotLoading(false);
        });
    }
  }, [
    clearAnnotationData,
    setIsSelectedShotLoading,
    setSelectedLayer,
    setSelectedShot,
    shotId,
  ]);

  useEffect(() => {
    if (selectedShot) {
      setLayers(undefined);
      setShotCutouts([]);
      setShowExportPanel(false);
      getLayers(selectedShot.id, service).then((result) => {
        setLayers(result);
      });
      getCutouts(undefined, selectedShot.id, service).then((result) => {
        setShotCutouts(result);
      });
    } else {
      setLayers(undefined);
      setShotCutouts([]);
    }
  }, [selectedShot, service, setLayers, setShotCutouts, setShowExportPanel]);

  return (
    <>
      <div className={styles.workspaceContainer}>
        {!isAnnotating && (
          <div className={styles.workspaceHeaderContainer}>
            <div className={styles.workspaceHeader}>
              <div>
                {selectedShot && selectedShot.is_hq != null && (
                  <div>
                    <label htmlFor="hqToggle">HQ</label>
                    <Switch
                      id="hqToggle"
                      className={styles.hqToggle}
                      checked={selectedShot.is_hq}
                      size="small"
                      color="primary"
                      onChange={() => {
                        setSelectedShot({
                          ...selectedShot,
                          is_hq: !selectedShot.is_hq,
                        });
                        updateUseHq(+selectedShot.id);
                      }}
                    />
                  </div>
                )}
                <ServiceDropdown />
              </div>

              <div className={styles.actions}>
                {selectedShot && service === SERVICES.SMART_VECTORS && (
                  <SmartVectorsHelpButton />
                )}
                {!!layersForExport.length && !showExportPanel && (
                  <ExportButton />
                )}

                {selectedShot && service === SERVICES.ROTOSCOPING && (
                  <>
                    {(() => {
                      const activeLayer = layerId
                        ? layers?.find((l) => l.id === +layerId)
                        : undefined;
                      const isAutopilotActive =
                        !!activeLayer?.is_autopilot_layer;
                      return (
                        <Guide
                          id={UserGuides.CreateAnnotation}
                          open={layers !== undefined && !layers.length}
                          placement="left"
                          title="Create your first annotation"
                          description="Select an optimal frame in the video and then click here to select an object."
                        >
                          <Button
                            className={cx(
                              styles.annotateButton,
                              "slapShotButton",
                            )}
                            variant="contained"
                            disabled={isAutopilotActive}
                            onClick={() => {
                              if (activeLayer) setSelectedLayer(activeLayer);
                              window.dispatchEvent(
                                new Event(WINDOW_EVENTS.ANNOTATE),
                              );
                            }}
                            color="secondary"
                            size="small"
                            sx={{
                              ".MuiSvgIcon-root": {
                                width: "14px",
                                height: "14px",
                              },
                            }}
                          >
                            <AddIcon />
                            Add Annotation
                          </Button>
                        </Guide>
                      );
                    })()}

                    {/* <RunAutopilot /> */}
                  </>
                )}

                {/* retime service is deprecated */}
                {/* {selectedShot && service === SERVICES.RETIME && <AddRetime />} */}

                {selectedShot && service === SERVICES.DEPTH_MAP && (
                  <CreateDepthMap />
                )}

                {selectedShot && service === SERVICES.SMART_VECTORS && (
                  <Guide
                    id={UserGuides.CreateSmartVectors}
                    open={layers !== undefined && !layers.length}
                    placement="left"
                    title="Add a region of interest"
                    description="Click here to pick a frame and draw a mask. Use Generate Motion Vectors in the layers panel when you are ready to run inference."
                  >
                    <Button
                      className={cx(styles.annotateButton, "slapShotButton")}
                      variant="contained"
                      onClick={() => {
                        requestSmartVectorsAddRoi();
                      }}
                      color="secondary"
                      size="small"
                      sx={{
                        ".MuiSvgIcon-root": {
                          width: "14px",
                          height: "14px",
                        },
                      }}
                    >
                      <AddIcon />
                      Add Region of Interest
                    </Button>
                  </Guide>
                )}

                <CameraTrack />
              </div>
            </div>
          </div>
        )}

        <div className={styles.workspaceArea}>
          {isSelectedShotLoading && !!selectedShot && (
            <div className={styles.loaderContainer}>
              <DotElastic />
            </div>
          )}

          {!imageData && !isSelectedShotLoading && selectedShot && !layerId && (
            <VideoPreview />
          )}

          {!selectedShot && (
            <div className={styles.emptyPlayer}>
              <span className={styles.selectShot}>
                Select a shot from the “Shots” panel on the left to view in the
                player.
              </span>
            </div>
          )}

          {!isAnnotating && selectedShot && layerId && shotId && (
            <ReviewPanel />
          )}

          {imageData && selectedShot && selectedFrame && isAnnotating && (
            <Workspace
              onBack={exitAnnotationMode}
              color={
                service === SERVICES.TRACKING
                  ? LAYER_COLORS[0]
                  : selectedLayer?.color ||
                    LAYER_COLORS[(layers?.length || 0) % LAYER_COLORS.length]
              }
            />
          )}
        </div>
      </div>

      {!showExportPanel || isAnnotating ? (
        <Layers ref={layersRef} />
      ) : (
        <ExportPanel />
      )}

      {service === SERVICES.SMART_VECTORS && (
        <SmartVectorsOutputGuideModal
          open={addRoiIntroModalOpen}
          onClose={() => setAddRoiIntroModalOpen(false)}
          onGotIt={addSmartVectorsRegionOfInterest}
        />
      )}
    </>
  );
};

const ShotWrapper = ({
  selectedShot,
  setSelectedShot,
  project,
  setShots,
  service,
  setService,
  subscription,
  ref,
}: ShotProps & { ref?: React.Ref<ShotRef> }) => {
  const { shotId, layerId } = useParams();
  const [isSelectedShotLoading, setIsSelectedShotLoading] = useState(false);
  const [layers, setLayers] = useState<LayerCompact[] | undefined>(undefined);
  const [cutouts, setCutouts] = useState<Cutout[]>([]);
  const [shotCutouts, setShotCutouts] = useState<Cutout[]>([]);
  const [cutoutInProgress, setCutoutInProgress] =
    useState<CutoutInProgress | null>(null);
  const [selectedCutout, setSelectedCutout] = useState<Cutout | null>(null);
  const [selectedLayer, setSelectedLayer] = useState<LayerCompact | null>(null);
  const [reviewLayer, setReviewLayer] = useState<LayerReview | null>(null);
  const [imageData, setImageData] = useState<string | null>(null);
  const [selectedFrame, setSelectedFrame] = useState<number | null>(null);
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [layersForExport, setLayersForExport] = useState<LayerCompact[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [showTrackingPanel, setShowTrackingPanel] = useState(false);
  const [throttleUsage, setThrottleUsage] = useState<ThrottleUsage | null>(
    null,
  );
  const [smartVectorsReviewPane, setSmartVectorsReviewPane] = useState<
    "inference_video" | null
  >(null);
  const shotRef = useRef<ShotRef>(null);
  const exitAnnotationModeRef = useRef<(() => void) | null>(null);
  const requestSmartVectorsAddRoiRef = useRef<(() => void) | null>(null);
  const [isAnnotationSaving, setIsAnnotationSaving] = useState(false);
  const isAnnotating = !!(imageData && selectedShot && selectedFrame);

  const clearAnnotationData = useCallback(() => {
    setImageData(null);
    setSelectedFrame(null);
    setCutoutInProgress(null);
    setIsAnnotationSaving(false);
  }, []);

  const getThrottleUsage = useCallback(() => {
    if (!subscription?.id) return;
    getThrottleUsageApi(subscription.id).then((result) => {
      setThrottleUsage(result);
    });
  }, [subscription?.id]);

  React.useImperativeHandle(ref, () => ({
    onInferenceLayerUpdate(id) {
      shotRef.current?.onInferenceLayerUpdate(id);
    },
  }));

  useEffect(() => {
    getThrottleUsage();
  }, [getThrottleUsage]);

  useEffect(() => {
    setShotCutouts((oldShotCutouts) => {
      const layerCutouts = new Set(cutouts.map((c) => c.id));
      const otherCutouts = oldShotCutouts.filter(
        (o) => !layerCutouts.has(o.id),
      );
      return [...cutouts, ...otherCutouts];
    });
  }, [cutouts]);

  const stableContextValue = useMemo(
    () => ({
      shotId,
      layerId,
      project,
      setShots,
      setSelectedShot,
      setIsSelectedShotLoading,
      setLayers,
      setCutouts,
      setShotCutouts,
      setCutoutInProgress,
      setSelectedCutout,
      setSelectedLayer,
      setReviewLayer,
      setImageData,
      setSelectedFrame,
      setShowExportPanel,
      setLayersForExport,
      setIsExporting,
      setIsAnnotationSaving,
      clearAnnotationData,
      service,
      setService,
      setShowTrackingPanel,
      getThrottleUsage,
      setSmartVectorsReviewPane,
      exitAnnotationModeRef,
      requestSmartVectorsAddRoiRef,
    }),
    [
      shotId,
      layerId,
      project,
      setShots,
      clearAnnotationData,
      service,
      setService,
      setSelectedShot,
      getThrottleUsage,
    ],
  );

  const contextValue: ShotContextType = useMemo(
    () => ({
      ...stableContextValue,
      selectedShot,
      isSelectedShotLoading,
      layers,
      cutouts,
      shotCutouts,
      cutoutInProgress,
      selectedCutout,
      selectedLayer,
      reviewLayer,
      imageData,
      selectedFrame,
      showExportPanel,
      layersForExport,
      isExporting,
      isAnnotating,
      isAnnotationSaving,
      showTrackingPanel,
      subscription,
      throttleUsage,
      smartVectorsReviewPane,
    }),
    [
      stableContextValue,
      selectedShot,
      isSelectedShotLoading,
      layers,
      cutouts,
      shotCutouts,
      cutoutInProgress,
      selectedCutout,
      selectedLayer,
      reviewLayer,
      imageData,
      selectedFrame,
      showExportPanel,
      layersForExport,
      isExporting,
      isAnnotating,
      isAnnotationSaving,
      showTrackingPanel,
      subscription,
      throttleUsage,
      smartVectorsReviewPane,
    ],
  );

  return (
    <ShotContext.Provider value={contextValue}>
      <Shot ref={shotRef} />
    </ShotContext.Provider>
  );
};

export default ShotWrapper;
